import { Component, EventEmitter, Output, HostListener, ElementRef, OnInit } from '@angular/core';
import { FilterSectionsToUpdate } from '../models/filter-import.models';
import { FilterService } from '../services/filter.service';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { PoeApiService, PoeFilter } from '../services/poe-api.service';
import { OAuthService } from '../services/o-auth.service';
import { LOCAL_STORAGE_KEY_ACCOUNT_FILTERS, LOCAL_STORAGE_KEY_FILTER_DEFAULT_REALM, LOCAL_STORAGE_KEY_LAST_LOADED_FILTERS, VERSION } from '../const';
import { Filter, StoredFilter } from '../models/filter-models';
import { totalRulesInCustomGroups } from '../common/custom-rules/custom-rule-groups-helper';
import { FilterImporterService } from '../services/filter-importer.service';
import { patchVersion } from '../../version';
import { FilterGeneratorService } from '../services/filter-generator.service';
import { logEvent } from '../app.config';
import { ImportFilterDialogComponent } from '../import-filter-dialog/import-filter-dialog.component';
import { TooltipWrapperComponent } from '../common/tooltip-wrapper/tooltip-wrapper.component';
import { FilterMigratorService } from '../services/filter-migrator.service';
import { ACCOUNT_FILTER_DEFAULT_REALM, DEFAULT_FILTER_NAME, FILTER_DESCRIPTION } from '../../game';
import { BaseComponent } from '../common/base.component';

@Component({
  selector: 'app-filter-selector-dialog',
  templateUrl: './filter-selector-dialog.component.html',
  styleUrls: ['../common/dialogs.scss', './filter-selector-dialog.component.scss'],
  imports: [FormsModule, DatePipe, ImportFilterDialogComponent, TooltipWrapperComponent]
})
export class FilterSelectorDialogComponent extends BaseComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @Output() updateFilter = new EventEmitter<FilterSectionsToUpdate>();

  oldName = '';
  renameError: string | null = null;
  loggedIn = false;
  showForceRefresh = false;

  retrievingFilters = false;
  filtersRetrieved = false;
  errorRetrievingFilters: string | null = null;

  downloadingFilter: PoeFilter | null = null;
  filterDownloaded = false;
  errorDownloadingFilters: string | null = null;

  uploadingFilter: StoredFilter | null = null;
  filterUploaded = false;
  errorUploadingFilter: string | null = null;

  allAccountFilters: PoeFilter[] = [];
  toolAccountFilters: PoeFilter[] = [];
  otherFilters: string | null = null;

  copyingSections = false;
  showImportDialog = false;
  fromFilter: StoredFilter | null = null;
  toFilter: StoredFilter | null = null;

  constructor(private elementRef: ElementRef, public filterService: FilterService,
    private loginService: OAuthService, private poeApiService: PoeApiService,
    private filterGeneratorService: FilterGeneratorService,
    private filterImporterService: FilterImporterService,
    private filterMigratorService: FilterMigratorService) {
    super();
  }

  customRuleTotal(f: Filter): number {
    return totalRulesInCustomGroups(f.customRuleGroups);
  }

  cosmeticRuleTotal(f: Filter): number {
    return totalRulesInCustomGroups(f.customCosmeticRuleGroups);
  }

  ngOnInit(): void {
    const lastLoadedFiltersString = localStorage.getItem(LOCAL_STORAGE_KEY_LAST_LOADED_FILTERS);
    const lastLoadedFiltersDate = lastLoadedFiltersString ? new Date(Number(lastLoadedFiltersString)) : null;

    if (!lastLoadedFiltersDate || new Date().getTime() - lastLoadedFiltersDate.getTime() > 1000 * 60 * 10) {
      if (this.loginService.userName()) {
        this.loggedIn = true;
        this.retrieveFilters();
      }
    } else {
      this.loggedIn = true;
      this.showForceRefresh = true;
      const storedAccountFilters = localStorage.getItem(LOCAL_STORAGE_KEY_ACCOUNT_FILTERS);
      if (storedAccountFilters) {
        this.allAccountFilters = JSON.parse(storedAccountFilters);
        this.buildAccountAndOtherFilters();
        this.filtersRetrieved = true;
      }
    }
  }

  retrieveFilters(event?: MouseEvent) {
    if (event) event.preventDefault();
    this.retrievingFilters = true;

    this.poeApiService.getFilters()
      .then(poeFilters => {
        this.allAccountFilters = poeFilters;
        localStorage.setItem(LOCAL_STORAGE_KEY_ACCOUNT_FILTERS , JSON.stringify(poeFilters));

        this.buildAccountAndOtherFilters();

        this.filtersRetrieved = true;
        this.showForceRefresh = false;
        localStorage.setItem(LOCAL_STORAGE_KEY_LAST_LOADED_FILTERS, Date.now().toString());
      })
      .catch(error => {
        this.errorRetrievingFilters = error;
      })
      .finally(() => {
        this.retrievingFilters = false;
      });
  }

  private buildAccountAndOtherFilters() {
    this.toolAccountFilters = this.allAccountFilters.filter(f =>
      f.description === FILTER_DESCRIPTION &&
      !this.filterService.storedFilters.find(sf => sf.id === f.id) &&
      f.version
    );

    this.otherFilters = this.allAccountFilters.filter(f => !this.filterService.storedFilters.find(sf => sf.id === f.id) &&
      (f.description !== FILTER_DESCRIPTION ||
        !f.version)
    ).map(f => f.filter_name).join(', ');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const dialogElement = this.elementRef.nativeElement.querySelector('.dialog');
    if (dialogElement && !dialogElement.contains(event.target)) {
      this.closeDialog();
    }
  }

  selectFilter(filterName: string) {
    this.filterService.setActiveFilter(filterName, 'stored');
    this.updateFilter.emit();
  }

  closeDialog() {
    this.close.emit();
  }

  nameChangeKeyDown(event: KeyboardEvent){
    const allowedChars = /^[a-zA-Z0-9 .-]$/;
    const char = event.key;

    // Allow navigation keys (arrows, backspace, delete, etc.)
    if (event.ctrlKey || event.altKey || event.metaKey ||
      char.length > 1 || char === 'Backspace' || char === 'Delete' ||
      char === 'ArrowLeft' || char === 'ArrowRight' ||
      char === 'Tab' || char === 'Home' || char === 'End') {
      return;
    }

    // Prevent input of disallowed characters
    if (!allowedChars.test(char)) {
      event.preventDefault();
    }
  }

  changeName(event: Event) {
    this.renameError = null;
    const newName = (event.target as HTMLInputElement).value;
    const existingNames = this.filterService.storedFilters.map(f => f.name).concat(this.allAccountFilters.map(f => f.filter_name));
    if (existingNames.filter(n => n === newName).length > 1) {
      (event.target as HTMLInputElement).value = this.oldName;
      this.renameError = `"${newName}" already exists. Please choose a different name.`;
      return;
    }
    this.filterService.updateFilterName(this.oldName, newName);
    this.filterService.saveFilters();
    logEvent('Filter', 'Name changed');
  }

  storeOldName(event: FocusEvent) {
    this.oldName = (event.target as HTMLInputElement).value;
  }

  selectAllText(event: FocusEvent) {
    (event.target as HTMLInputElement).select();
  }

  login($event: any) {
    $event.preventDefault();
    this.loginService.login();
  }

  upload(event: MouseEvent, storedFilterToUpload: StoredFilter) {
    event.preventDefault();
    event.stopPropagation();

    const realm = localStorage.getItem(LOCAL_STORAGE_KEY_FILTER_DEFAULT_REALM) || ACCOUNT_FILTER_DEFAULT_REALM;
    this.uploadingFilter = storedFilterToUpload;
    const currentFilter = this.filterService.activeFilter();
    this.filterService.setActiveFilter(storedFilterToUpload.name, 'upload');
    const generatedFilter = this.filterGeneratorService.updateFilter(storedFilterToUpload, true);
    this.filterService.setActiveFilter(currentFilter.name, 'active');
    if (storedFilterToUpload.id) {
      this.poeApiService.updateFilter(storedFilterToUpload.id, storedFilterToUpload.name, generatedFilter.filterTextFull, realm)
        .then(() => {
          logEvent('Filter', 'Upload update');
        })
        .catch(error => {
          this.errorUploadingFilter = (error?.toString() || 'Unknown error').substring(0, 100);
          logEvent('Filter', 'Upload update error', this.errorUploadingFilter ?? undefined);
        })
        .finally(() => {
          this.uploadingFilter = null;
        });
    } else {
      this.poeApiService.createFilter(storedFilterToUpload.name, generatedFilter.filterTextFull, realm)
        .then(createdFilter => {
          storedFilterToUpload.id = createdFilter.id;
          this.filterService.setFilterId(storedFilterToUpload.name, storedFilterToUpload.id);
          this.allAccountFilters.push({
            id: storedFilterToUpload.id,
            filter_name: storedFilterToUpload.name,
            realm,
            description: FILTER_DESCRIPTION,
            version: storedFilterToUpload.version,
            type: 'Normal',
            public: true,
          });
          localStorage.setItem(LOCAL_STORAGE_KEY_ACCOUNT_FILTERS, JSON.stringify(this.allAccountFilters));
          this.buildAccountAndOtherFilters();
          logEvent('Filter', 'Upload initial on ' + realm);
        })
        .catch(error => {
          this.errorUploadingFilter = (error?.toString() || 'Unknown error').substring(0, 100);
          logEvent('Filter', 'Upload initial error', this.errorUploadingFilter ?? undefined);
        })
        .finally(() => {
          this.uploadingFilter = null;
        });
    }
  }

  download(accountFilter: PoeFilter) {
    this.downloadingFilter = accountFilter;
    this.filterDownloaded = false;
    this.poeApiService.getFilter(accountFilter.id)
      .then(details => {
        try {
          const importResult = this.filterImporterService.importFilter(details.filter!);
          if (importResult.importFilterError) {
            this.errorDownloadingFilters = importResult.importFilterError;
            return;
          } else {
            // Parse patchVersion from version string (handles both old "major.minor.patch" and new "major.minor.patch.build" formats)
            // Note: The 'build' field stores the patchVersion, not the build number
            const versionParts = details.version.split('.');
            const patchVersion = versionParts.length === 4
              ? Number(versionParts[2]) // New format: use 3rd part (patchVersion)
              : Number(versionParts[versionParts.length - 1] || '0'); // Old format: use last part (patchVersion)

            const storedFilter = {
              name: details.filter_name,
              filter: importResult.filter,
              version: details.version,
              build: patchVersion,
              lastUpdated: new Date().toISOString(),
              id: details.id,
            } as StoredFilter;
            this.filterService.addFilter(storedFilter);
            this.filterDownloaded = true;
            this.toolAccountFilters = this.toolAccountFilters.filter(f => f.id !== accountFilter.id);
            logEvent('Filter', 'Download');
          }
        } catch (error) {
          this.errorDownloadingFilters = (error?.toString() || 'Unknown error').substring(0, 100);
          logEvent('Filter', 'Download error', this.errorDownloadingFilters ?? undefined);
        }
      })
      .catch(error => {
        this.errorDownloadingFilters = (error?.toString() || 'Unknown error').substring(0, 100);
        logEvent('Filter', 'Download error', this.errorDownloadingFilters ?? undefined);
      })
      .finally(() => {
        this.downloadingFilter = null;
      });
  }

  duplicate(event: MouseEvent, filter: StoredFilter) {
    event.preventDefault();
    event.stopPropagation();

    let newName = filter.name + ' - copy';
    let counter = 1;
    while (this.filterService.storedFilters.some(f => f.name === newName)) {
      newName = `${filter.name} - copy ${counter++}`;
    }

    const newFilter = new StoredFilter(newName, this.filterService.createNewFilter(), filter.version, filter.build, filter.lastUpdated);
    this.toFilter = newFilter;
    this.fromFilter = filter;
    this.showImportDialog = true;
  }

  copySections(event: MouseEvent, filter: StoredFilter) {
    event.preventDefault();
    event.stopPropagation();

    if (filter!.build < patchVersion) {
      this.filterMigratorService.migrateFilter(filter!.filter, filter!.build, 'copy');
      filter!.version = VERSION;
      filter!.build = patchVersion;
    }

    this.copyingSections = true;
    this.fromFilter = filter;
  }

  pasteSections(event: MouseEvent, filter: StoredFilter) {
    event.preventDefault();
    event.stopPropagation();

    this.toFilter = filter;
    this.showImportDialog = true;
  }

  closeImportDialog() {
    this.showImportDialog = false;
  }

  updateFilterSections(sections: FilterSectionsToUpdate) {
    if (this.toFilter!.build < patchVersion) {
      this.filterMigratorService.migrateFilter(this.toFilter!.filter, this.toFilter!.build, this.copyingSections ? 'copy' : 'duplicate');
      this.toFilter!.version = VERSION;
      this.toFilter!.build = patchVersion;
    }

    this.filterService.updateFilterSections(this.fromFilter!.filter, sections, this.toFilter!.filter);

    if (!this.copyingSections) {
      this.filterService.addFilter(this.toFilter!);
      logEvent('Filter', 'Duplicate');
    } else {
      logEvent('Filter', 'Copy Sections');
    }

    this.toFilter = null;
    this.fromFilter = null;
    this.showImportDialog = false;
    this.copyingSections = false;
  }

  remove(event: MouseEvent, filter: StoredFilter) {
    event.preventDefault();
    event.stopPropagation();

    this.filterService.removeFilter(filter.name);

    const remaining = this.filterService.storedFilters;
    if (remaining.length > 0) {
      this.filterService.setActiveFilter(remaining[0].name, 'active');
    } else {
      const storedFilter: StoredFilter = {
        name: DEFAULT_FILTER_NAME,
        filter: this.filterService.createNewFilter(),
        lastUpdated: new Date().toISOString(),
        version: VERSION,
        build: patchVersion
      };
      this.filterService.addFilter(storedFilter);
      this.filterService.setActiveFilter(storedFilter.name, 'active');
    }

    this.buildAccountAndOtherFilters();
    logEvent('Filter', 'Remove');
  }

  createNew() {
    let uniqueName = 'New Filter';
    let counter = 1;
    while (this.filterService.storedFilters.some(f => f.name === uniqueName)) {
      uniqueName = `New Filter ${counter++}`;
    }

    const newFilter = {
      name: uniqueName,
      filter: this.filterService.createNewFilter(),
      version: VERSION,
      build: patchVersion,
      lastUpdated: new Date().toISOString(),
    } as StoredFilter;

    this.filterService.addFilter(newFilter);
    logEvent('Filter', 'New');
  }
}
